import {
	ButtonArrowIcon,
	ClearIcon,
	FilterIcon,
	SearchIcon,
} from "@/comps/ai-tools/icons"
import { DropDown } from "@/comps/dropdown"
import Footer from "@/comps/footer"
import Header from "@/comps/header"
import Meta, { GalleryMetaProps } from "@/comps/meta"
import { SizedSkeleton } from "@/comps/skeleton"
import { axios } from "@/utils/axios-wrapper"
import { assetUrl } from "@/utils/cdn"
import {
	AdExamplesViewProvider,
	useAdExamplesViewContext,
} from "@/utils/discover"
import queryfy from "@/utils/queryfy"
import clsx from "clsx"
import { SuggestionsResponse } from "models/discover"
import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"
import {
	AdExamplesType,
	FilterStateAdExamples,
	indexFiltersAdExamples,
	MetaLinks,
} from "pages/ad-examples"
import {
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react"
import { useQuery, useQueryClient } from "react-query"
import { Thing, VideoObject, WithContext } from "schema-dts"
import {
	AdExamplesGrid,
	FilterDialogAdExamples,
	RatioAdExamples,
} from "./ad-examples"
import {
	AdExamplesPopup,
	AdExamplesPopupProps,
} from "./ad-examples-popup"

function getSuggestions(query: string) {
	return async function () {
		const endpoint = "/api/v1/wizard/gallery/suggestions"
		return await axios
			.get<SuggestionsResponse>(endpoint, {
				params: { query },
			})
			.then((response) => response.data.suggestions)
	}
}

export function getUrlFromString(text: string) {
	return text
		.trim()
		.replace(/,/g, "-") // Remove all commas
		.replace(/&/g, "and") // Replace all "&" with "and"
		.replace(/\s\s*/g, "-") // Replace all spaces with hyphens
		.replace(/\//g, "-") // Replace all "/" with "-"
		.replace(/-{1,}/g, "-") // Replace sequences of two or more hyphens with a single hyphen
		.toLowerCase()
}

export type Pages = "ad-examples"

function getJsonLDDataAdExamples(
	query: string,
	jobs: AdExamplesType | null,
): (WithContext<Thing> | GalleryMetaProps)[] {
	const jsonLdData: (
		| WithContext<Thing>
		| GalleryMetaProps
	)[] = []

	if (query.trim().length !== 0) {
		jsonLdData.push({
			"@context": "https://schema.org",
			"@type": "WebSite",
			url: "https://vidsup.com/ad-examples/",
			potentialAction: {
				"@type": "SearchAction",
				target:
					"https://vidsup.com/ad-examples/{search_term_string}",
				"query-input": "required name=search_term_string",
			},
		})
	}

	if (jobs) {
		if (jobs.type === "home") {
			jobs.content.forEach((page) => {
				page.results.forEach((job) => {
					if (!job.preview_video_url) return

					const imageObject: WithContext<VideoObject> = {
						"@context": "https://schema.org",
						"@type": "VideoObject",
						name: job.title ?? job.description ?? "",
						description: job.description ?? job.title ?? "",
						thumbnailUrl: job.preview_thumbnail_url,
						license: "https://vidsup.com/privacy-policy",
						acquireLicensePage:
							"https://vidsup.com/ad-examples/single/" +
							job.id,
						copyrightNotice: "Vidsup",
						uploadDate: job.created_at,
						author: {
							"@type": "Person",
							name: job.owner?.username ?? "Vidsup",
						},
						creator: {
							"@type": "Person",
							name: job.owner?.username ?? "Vidsup",
						},
						contentUrl: job.preview_video_url,
					}
					jsonLdData.push(imageObject)
				})
			})
		} else {
			jobs.content.forEach((job) => {
				if (!job.preview_video_url) return

				const imageObject: WithContext<VideoObject> = {
					"@context": "https://schema.org",
					"@type": "VideoObject",
					name: job.title ?? job.description ?? "",
					description: job.description ?? job.title ?? "",
					thumbnailUrl: job.preview_thumbnail_url,
					license: "https://vidsup.com/privacy-policy",
					acquireLicensePage:
						"https://vidsup.com/ad-examples/single/" +
						job.id,
					copyrightNotice: "Vidsup",
					uploadDate: job.created_at,
					author: {
						"@type": "Person",
						name: job.owner?.username,
					},
					creator: {
						"@type": "Person",
						name: job.owner?.username,
					},
					contentUrl: job.preview_video_url,
				}
				jsonLdData.push(imageObject)
			})
		}
	}

	return jsonLdData
}

interface GalleryProps {
	adExamplesJobs: AdExamplesType | null
	initialFiltersAdExamples: FilterStateAdExamples | null
	pageTitle: string | null
	initialPage: Pages
	metaLinks: MetaLinks | null
	pageNumber?: number
}

export default function GalleryAll(props: GalleryProps) {
	const [filterDialogOpen, setFilterDialogOpen] =
		useState(false)
	const [isFilterSelected, setIsFilterSelected] =
		useState(false)
	const { t } = useTranslation("common")
	const {
		initialFiltersAdExamples,
		adExamplesJobs,
		pageTitle: title,
		initialPage,
		metaLinks,
		pageNumber,
	} = props
	const [adExamplesPopupState, setAdExamplesPopupState] =
		useState<AdExamplesPopupProps | null>(null)
	const [pageTitle, setPageTitle] = useState(title)
	const [active, setActive] = useState(false)
	const [page, setPage] = useState(initialPage)
	const [searchByPage, setSearchByPage] =
		useState(initialPage)
	const router = useRouter()

	const [queryInput, setQueryInput] = useState(
		router.asPath.split(`/${page}/`)[1]?.split("?")[0] ??
			"",
	)

	const [pageLinks, setPageLinks] = useState<{
		next: string | null
		prev: string | null
	}>(
		metaLinks ?? {
			next: `${router.asPath}?page=2`,
			prev: null,
		},
	)

	const prevUrlRef = useRef<string | null>(null)

	useEffect(() => {
		if (typeof window !== "undefined") {
			prevUrlRef.current = window.location.href

			const handlePopState = () => {
				if (window.location.href !== prevUrlRef.current) {
					window.location.reload()
				}
			}

			window.addEventListener("popstate", handlePopState)

			return () => {
				window.removeEventListener(
					"popstate",
					handlePopState,
				)
			}
		}
	}, [])

	useEffect(() => {
		if (typeof window !== "undefined") {
			prevUrlRef.current = window.location.href
		}
	})

	useEffect(() => {
		if (filterDialogOpen || adExamplesPopupState !== null) {
			document.body.style.overflow = "hidden"
		} else {
			document.body.style.overflow = "unset"
		}
	}, [adExamplesPopupState, filterDialogOpen])

	return (
		<AdExamplesViewProvider
			initialFilters={
				initialFiltersAdExamples ?? indexFiltersAdExamples
			}
			initialData={adExamplesJobs ?? undefined}
			page={page}
			pageNumber={
				initialPage === "ad-examples" && pageNumber
					? pageNumber
					: 1
			}>
			<Meta
				title={
					pageTitle
						? pageTitle + " " + t("txt_ad_examples_vidsup")
						: t("txt_ad_examples_title")
				}
				description={t("txt_ad_examples_description")}
				cover={assetUrl("/covers/ad-examples-new.jpg")}
				prevAndNextLinks={
					page === "ad-examples" ? pageLinks : undefined
				}
				currentPage={router.asPath}
				jsonLdData={
					page === "ad-examples"
						? getJsonLDDataAdExamples(
								queryInput,
								adExamplesJobs,
						  )
						: []
				}
			/>
			<Header isStatic isSolid />
			<>
				<FilterDialogMobile
					filterDialogOpen={filterDialogOpen}
					setFilterDialogOpen={setFilterDialogOpen}
				/>
			</>

			<div className="flex min-h-screen flex-col justify-between overflow-x-clip">
				<div className="sticky top-[-20px] z-50 bg-color-background tablet:top-[16px] desktop:top-[10px] ">
					<div
						style={{
							background:
								"linear-gradient(90deg, rgba(176, 191, 232, 0.18) 12.5%, rgba(229, 138, 88, 0.12) 51.98%, rgba(214, 51, 168, 0.11) 86.46%)",
						}}
						className={clsx(
							"flex h-[194px] w-full flex-col items-center justify-center gap-[14px] px-4",
							"tablet:h-[176px] desktop:h-[190px] desktop:px-8",
						)}>
						<div
							className={clsx(
								"z-[1] flex w-full flex-row items-start justify-between pt-[16px] tablet:pt-[18px] desktop:pt-0",
								"tablet:w-[768px] tablet:px-4 desktop:w-[1175px] desktop:px-0",
							)}>
							<div className="flex w-full flex-col justify-end gap-5 tablet:w-auto">
								<PageTitle
									page={page}
									pageTitle={pageTitle}
									setPageTitle={setPageTitle}
								/>
								{/* <div className="flex flex-row items-center gap-[18px] tablet:gap-[22px] desktop:gap-8">
										<a
											href="/ad-examples"
											onClick={(e) => {
												e.preventDefault()
												window.history.pushState(
													{ path: "/ad-examples" },
													"",
													"/ad-examples",
												)
												setPage("ad-examples")
												setSearchByPage("ad-examples")
											}}
											className={clsx(
												"truncate text-[18px] font-600 desktop:text-[21px]",
												"leading-[36px] transition-colors",
												"flex flex-row items-center gap-1",
												page === "ad-examples"
													? "text-primary-500 [--icon-color:var(--color-primary-500)]"
													: "text-blue-700 [--icon-color:var(--color-blue-700)]",
											)}>
											{aiAdsGenratorIcon()}
											{t("lbl_ad_examples")}
										</a>
									</div> */}
								<div className="flex flex-row items-center gap-2">
									<div
										className={clsx(
											"relative flex h-11 w-full flex-row items-center",
											"gap-[6px] rounded-[6px] bg-color-cell px-3 py-[10px]",
											"dark:bg-color-background tablet:h-10 tablet:w-[394px]",
											"tablet:rounded-[8px] tablet:py-[7px] tablet:pl-0 tablet:pr-[14px]",
											"border transition-colors desktop:h-11 desktop:w-[620px]",
											"desktop:rounded-[10px] desktop:py-[10px]",
											active
												? " border-blue-400"
												: "border-color-cell dark:border-color-background",
										)}>
										<div
											className={clsx(
												" absolute -left-1 -top-1 -z-[1] h-[calc(100%+8px)] w-[calc(100%+8px)] bg-color-black/20 transition-opacity",
												"rounded-[9px] tablet:rounded-[11px] desktop:rounded-[13px]",
												active
													? "opacity-100"
													: "opacity-0",
											)}></div>

										{/* <SearchBy
											searchByPage={searchByPage}
											setSearchByPage={setSearchByPage}
										/> */}

										{/* <div
											className={clsx(
												"ml-[2px] hidden h-full w-[1px] border-l",
												"border-color-separator tablet:block",
											)}
										/> */}

										<div
											className={clsx(
												"flex h-[16px] w-[16px] items-center justify-center",
												"[--icon-color:var(--color-placeholder)] tablet:ml-[4px]",
											)}>
											<SearchIcon />
										</div>

										<SearchQuery
											setPageTitle={setPageTitle}
											setActive={setActive}
											searchByPage={searchByPage}
											setPage={setPage}
											setQueryInput={setQueryInput}
										/>
									</div>
									<div className="relative flex h-11 tablet:hidden">
										<button
											type="button"
											onClick={() =>
												setFilterDialogOpen(true)
											}
											className={clsx(
												"flex flex-row items-center justify-center",
												"[--icon-color:var(--color-blue-600)]",
												"h-full w-11 hover:bg-blue-100",
												"rounded-[6px] bg-color-cell dark:bg-color-background",
											)}>
											<FilterIcon />
										</button>
										<div
											className={clsx(
												"absolute right-[-3px] top-[-3px] h-[8px] w-[8px]",
												"rounded-full bg-primary-500 transition-opacity tablet:hidden",
												!isFilterSelected && "opacity-0",
											)}
										/>
									</div>
								</div>
							</div>

							<div className="hidden h-full flex-row items-end gap-3 tablet:flex">
								{page === "ad-examples" && (
									<RatioAdExamples />
								)}
							</div>
						</div>

						{page === "ad-examples" && (
							<GallerySuggestions
								setPageTitle={setPageTitle}
								isAdExamples
							/>
						)}
					</div>
				</div>

				{page === "ad-examples" && (
					<AdExamplesGrid
						setPopupState={setAdExamplesPopupState}
					/>
				)}

				<Footer />
			</div>

			{adExamplesPopupState !== null && (
				<AdExamplesPopup {...adExamplesPopupState} />
			)}
		</AdExamplesViewProvider>
	)
}

interface PageTitleProps {
	pageTitle: string | null
	page: Pages
	setPageTitle: (v: string | null) => void
}

function PageTitle(props: PageTitleProps) {
	const { page, pageTitle, setPageTitle } = props
	const { t } = useTranslation()
	const { query } = useAdExamplesViewContext()

	const [oldTitle, setOldTitle] = useState(pageTitle)
	const totalAdExamples =
		query.data?.pages[0].data.type === "searched"
			? query.data.pages[0].data.total
			: null

	const number =
		page === "ad-examples" ? totalAdExamples : null

	useEffect(() => {
		if (oldTitle !== pageTitle && number !== null) {
			if (query.data?.pages[0].data.content.length === 0)
				return

			const newTitle = number + " " + pageTitle
			setPageTitle(newTitle)
			setOldTitle(newTitle)
		}
	}, [number, oldTitle, page, pageTitle, setPageTitle])

	return (
		<h1
			className={clsx(
				"text-h5 w-full truncate text-center text-blue-800",
				"tablet:w-[482px] tablet:text-start tablet:leading-[24px] desktop:w-[750px] desktop:leading-[36px]",
			)}>
			{pageTitle
				? pageTitle + " " + t("lbl_ad_examples")
				: t("lbl_ad_examples")}
		</h1>
	)
}

interface GallerySuggestionsProps {
	setPageTitle?: (id: string | null) => void
	isColorBackground?: boolean
	isAdExamples?: boolean
	changeDiscoverFilter?: (filter: Pages, q?: string) => void
}

const MAX_ALLOWED_DISTANCE = 1
function getNextVisibleChild(
	container: HTMLDivElement,
): HTMLElement | null {
	const parentBounds = container.getBoundingClientRect()
	const parentX = parentBounds.x
	const parentWidth = parentBounds.width

	for (const child of container.childNodes) {
		if (child instanceof HTMLElement) {
			const { x, width } = child.getBoundingClientRect()
			if (
				x + width - MAX_ALLOWED_DISTANCE >
				parentX + parentWidth
			) {
				return child
			}
		}
	}

	return null
}

function getPrevVisibleChild(
	container: HTMLDivElement,
): HTMLButtonElement | null {
	const parentBounds = container.getBoundingClientRect()
	const parentX = parentBounds.x
	let last: HTMLButtonElement | null = null
	for (const child of container.childNodes) {
		if (child instanceof HTMLButtonElement) {
			const { x } = child.getBoundingClientRect()
			if (x + MAX_ALLOWED_DISTANCE > parentX) {
				return last
			}
			last = child
		}
	}

	return null
}

export function GallerySuggestions(
	props: GallerySuggestionsProps,
) {
	const {
		setPageTitle,
		isAdExamples,
		isColorBackground,
		changeDiscoverFilter,
	} = props
	const {
		state: adExamplesState,
		dispatch: adExamplesDispatch,
	} = useAdExamplesViewContext()
	const { searchQuery } = adExamplesState

	const router = useRouter()
	const scrollContainerRef = useRef<HTMLDivElement>(null)
	const [prevDisabled, setPrevDisabled] = useState(true)
	const [nextDisabled, setNextDisabled] = useState(false)
	const scrollLeft = () => {
		if (!scrollContainerRef.current) {
			return
		}
		const prev = getPrevVisibleChild(
			scrollContainerRef.current,
		)
		if (!prev) {
			return
		}

		prev.scrollIntoView({
			block: "center",
			inline: "start",
			behavior: "smooth",
		})
	}

	const scrollRight = () => {
		if (!scrollContainerRef.current) {
			return
		}
		const next = getNextVisibleChild(
			scrollContainerRef.current,
		)
		if (!next) {
			return
		}

		next.scrollIntoView({
			block: "center",
			inline: "end",
			behavior: "smooth",
		})
	}

	const submitQuery = (suggestion: string) => {
		if (isAdExamples) {
			adExamplesDispatch({
				type: "set_search_query",
				searchQuery: suggestion,
			})
			suggestions.refetch()
			adExamplesDispatch({
				type: "apply_filters",
			})

			changeDiscoverFilter &&
				changeDiscoverFilter("ad-examples", suggestion)
		}
	}

	const updateButtonStatus = useCallback(() => {
		let nextDisabled = false
		if (
			scrollContainerRef.current &&
			scrollContainerRef.current.scrollWidth <=
				scrollContainerRef.current.clientWidth
		) {
			nextDisabled = true
		}

		if (
			scrollContainerRef.current &&
			getNextVisibleChild(scrollContainerRef.current)
		) {
			nextDisabled = false
		}

		if (
			scrollContainerRef.current &&
			!getNextVisibleChild(scrollContainerRef.current)
		) {
			nextDisabled = true
		}

		setNextDisabled(nextDisabled)

		let prevDisabled = true
		if (
			scrollContainerRef.current &&
			getPrevVisibleChild(scrollContainerRef.current)
		) {
			prevDisabled = false
		}

		setPrevDisabled(prevDisabled)
	}, [scrollContainerRef])

	const suggestions = useQuery({
		queryKey: [
			"suggestions",
			`query-${searchQuery}`,
			`is-ad-examples-${isAdExamples}`,
		],
		queryFn: getSuggestions(searchQuery ?? ""),
		staleTime: Infinity,
	})

	useEffect(() => {
		updateButtonStatus()
	}, [searchQuery, updateButtonStatus, suggestions.status])

	return (
		<>
			<div
				className={clsx(
					"relative w-full",
					!isColorBackground && " desktop:w-[1175px]",
				)}>
				<div
					onScroll={updateButtonStatus}
					ref={scrollContainerRef}
					className={clsx(
						"no-scrollbar mx-auto flex w-full flex-row",
						"items-center gap-[10px] overflow-x-scroll",
						isColorBackground
							? "w-full"
							: "tablet:px-[calc(50%-368px)] desktop:w-[1175px] desktop:px-0",
						isColorBackground &&
							suggestions.data?.length === 0
							? "h-0"
							: "h-[36px]",
					)}>
					{suggestions.isFetching && (
						<SuggestionsSkeleton />
					)}
					{suggestions.isSuccess &&
						!suggestions.isFetching &&
						suggestions.data.map((suggestion) => {
							const searchUrl = getUrlFromString(suggestion)

							const href = isAdExamples
								? `/ad-examples/${encodeURIComponent(
										searchUrl,
								  )}`
								: `/ai-gallery/${encodeURIComponent(
										searchUrl,
								  )}`

							return (
								<a
									href={href}
									key={suggestion}
									onClick={(e) => {
										e.preventDefault()
										submitQuery(suggestion)

										if (setPageTitle) {
											setPageTitle(
												suggestion
													.replace("-", " ")
													.replace(/\b\w/g, (match) =>
														match.toUpperCase(),
													),
											)

											router.push(href, undefined, {
												shallow: true,
											})
										}
									}}
									className={clsx(
										"flex h-full shrink-0 flex-row items-center justify-center",
										"gap-[4px] rounded-[6px] p-3 text-[13px]",
										"font-400 tracking-wider text-blue-700 [--icon-color:var(--color-blue-600)]",
										isColorBackground
											? "bg-blue-50"
											: "bg-color-cell dark:bg-color-background",
									)}>
									<div className="flex h-[14px] w-[14px] items-center justify-center">
										{SearchIcon()}
									</div>
									{suggestion}
								</a>
							)
						})}
				</div>

				{/* Left Button */}

				<button
					disabled={prevDisabled}
					style={{
						filter:
							"drop-shadow(0px 2.353px 13.647px rgba(123, 128, 158, 0.12))",
					}}
					className={clsx(
						"absolute top-[-2px] hidden h-10 w-10",
						"items-center justify-center rounded-full bg-color-cell",
						"[--icon-color:var(--color-blue-600)] disabled:pointer-events-none",
						"disabled:opacity-0 desktop:flex",
						suggestions.isFetching &&
							"pointer-events-none opacity-0",
						isColorBackground
							? "left-[-10px]"
							: "left-[-20px]",
					)}
					onClick={scrollLeft}>
					{ButtonArrowIcon()}
				</button>

				{/* Right Button */}

				<button
					disabled={nextDisabled}
					style={{
						filter:
							"drop-shadow(0px 2.353px 13.647px rgba(123, 128, 158, 0.12))",
					}}
					className={clsx(
						"absolute top-[-2px] hidden h-10 w-10",
						"rotate-180 items-center justify-center rounded-full bg-color-cell",
						"[--icon-color:var(--color-blue-600)] disabled:pointer-events-none",
						"disabled:opacity-0 desktop:flex",
						suggestions.isFetching &&
							"pointer-events-none opacity-0",
						isColorBackground
							? "right-[-10px]"
							: "right-[-20px]",
					)}
					onClick={scrollRight}>
					{ButtonArrowIcon()}
				</button>
			</div>
		</>
	)
}

export function SuggestionsSkeleton() {
	return (
		<>
			<SizedSkeleton className="h-full w-[110px] shrink-0 rounded-[6px]"></SizedSkeleton>
			<SizedSkeleton className="h-full w-[80px] shrink-0 rounded-[6px]"></SizedSkeleton>
			<SizedSkeleton className="h-full w-[90px] shrink-0 rounded-[6px]"></SizedSkeleton>
			<SizedSkeleton className="h-full w-[70px] shrink-0 rounded-[6px]"></SizedSkeleton>
			<SizedSkeleton className="h-full w-[110px] shrink-0 rounded-[6px]"></SizedSkeleton>
			<SizedSkeleton className="h-full w-[90px] shrink-0 rounded-[6px]"></SizedSkeleton>
		</>
	)
}

interface SearchQueryProps {
	setPageTitle: (id: string | null) => void
	setActive: (state: boolean) => void
	searchByPage: Pages
	setPage: (page: Pages) => void
	setQueryInput: (query: string) => void
}

function SearchQuery(props: SearchQueryProps) {
	const {
		setPageTitle,
		setActive,
		setPage,
		searchByPage,
		setQueryInput,
	} = props

	const {
		state: adExamplesState,
		dispatch: adExamplesDispatch,
	} = useAdExamplesViewContext()

	const state = adExamplesState

	const { searchQuery } = state
	const queryClient = useQueryClient()
	const [value, setValue] = useState("")

	useEffect(() => {
		if (searchQuery) {
			setValue(searchQuery)
		}
	}, [searchQuery])

	const submitQuery = (clear?: boolean) => {
		const text = clear ? "" : value

		adExamplesDispatch({
			type: "set_search_query",
			searchQuery: text.trim().length === 0 ? null : text,
		})
		queryClient.invalidateQueries("suggestions")

		if (searchByPage === "ad-examples") {
			adExamplesDispatch({
				type: "apply_filters",
			})
		}

		const url = new URL(window.location.href)
		const searchParams = new URLSearchParams(url.search)
		const category = searchParams.get("categoryId")

		const params = category
			? queryfy({
					categoryId: category,
			  })
			: ""

		if (text.trim().length === 0) {
			setPageTitle(null)

			const newUrl = `/${searchByPage}${params}`
			setQueryInput("")
			setPage(searchByPage)
			window.history.pushState({ path: newUrl }, "", newUrl)
		} else {
			setPageTitle(
				text
					.replace("-", " ")
					.replace(/\b\w/g, (match) => match.toUpperCase()),
			)

			const searchUrl = getUrlFromString(text)

			const newUrl = `/${searchByPage}/${encodeURIComponent(
				searchUrl,
			)}${params}`
			setQueryInput(searchUrl)
			setPage(searchByPage)

			window.history.pushState({ path: newUrl }, "", newUrl)
		}
	}

	return (
		<div className="relative flex-1">
			<input
				type="text"
				name="search art"
				id="search art"
				onChange={(e) => setValue(e.currentTarget.value)}
				onFocus={() => {
					setActive(true)
				}}
				onBlur={() => {
					setActive(false)
				}}
				value={value}
				placeholder={"Search ads"}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						submitQuery()
					}
				}}
				className={clsx(
					"w-[calc(100%-23px)] border-none bg-[transparent] text-[16px] font-500 text-blue-900",
					"placeholder:text-color-placeholder focus:outline-none",
				)}
			/>

			<button
				onClick={() => {
					setValue("")
					if (searchQuery) {
						submitQuery(true)
					}
				}}
				className={clsx(
					"absolute right-0 top-[calc(50%-11px)] flex h-[22px] w-[22px] items-center justify-center",
					"rounded-full bg-blue-200 transition-opacity [--icon-color:var(--color-blue-600)] hover:bg-blue-300",
					value.trim().length !== 0
						? "opacity-100"
						: "pointer-events-none opacity-0",
				)}>
				{ClearIcon()}
			</button>
		</div>
	)
}

interface SearchByProps {
	setSearchByPage: (page: Pages) => void
	searchByPage: Pages
}

export function SearchBy(props: SearchByProps) {
	const { searchByPage, setSearchByPage } = props

	const { t } = useTranslation()

	return (
		<DropDown
			hover
			className="group hidden h-full tablet:block"
			trigger={
				<div
					className={clsx(
						"flex w-[120px] flex-row items-center justify-between gap-[6px] pl-[14px]",
					)}>
					<p className="flex-1 text-center text-[15px] font-500 text-blue-700">
						{t(`lbl_${searchByPage.replaceAll("-", "_")}`)}
					</p>
					<div
						className={clsx(
							"mr-1 flex -rotate-90 items-center justify-center",
							"transition-all [--icon-color:var(--color-placeholder)] group-hover:-rotate-[270deg]",
						)}>
						<ButtonArrowIcon />
					</div>
				</div>
			}>
			<div className="absolute z-10 w-[134px] pt-5">
				<div
					className={clsx(
						"flex flex-col items-center gap-1 rounded-[12px] bg-color-popup-cell",
						"px-[10px] py-[14px] [box-shadow:0px_0px_10px_rgba(0,0,0,0.13)]",
					)}>
					<div
						onClick={() => setSearchByPage("ad-examples")}
						className={clsx(
							"flex w-full cursor-pointer py-2 pl-4",
							"rounded-[8px] text-[14px] font-500 text-blue-800 transition-all",
							searchByPage === "ad-examples"
								? "bg-blue-100 hover:bg-blue-100"
								: "hover:bg-color-background",
						)}>
						{t("lbl_ad_examples")}
					</div>
				</div>
			</div>
		</DropDown>
	)
}

interface FilterDialogMobileProps {
	filterDialogOpen: boolean
	setFilterDialogOpen: (open: boolean) => void
}

export function FilterDialogMobile(
	props: FilterDialogMobileProps,
) {
	const { filterDialogOpen, setFilterDialogOpen } = props
	return (
		<FilterDialogAdExamples
			isOpen={filterDialogOpen}
			setIsOpen={setFilterDialogOpen}
		/>
	)
}
